/**
 * voice-v8 — Zugangswerte ohne Modell-Kontext: was Karte (frontend/lib/karten/zugangswerte.ts), Server
 * (server/zugangswert-ablage.ts, server/extension-hub.ts) und Speicher (memory/store-sqlite.ts) gemeinsam wissen.
 * (27.10.2026, Autonomie-Modus B9, Sicherheitsprüfung S4)
 *
 * ## Der Anlass (S4)
 *
 * Vespera las ein SMTP-Passwort mit browser_get_page_text oder einem Bildschirmfoto und reichte es als `wert` an
 * `schluessel_ablegen` weiter. Damit stand der Wert im Modellkontext, im Protokoll des Anbieters und im Verlauf.
 *
 * ## Der neue Weg (nur mit ZUGANGSWERT_ABLAGE=1, Vorgabe AUS)
 *
 *  1. `browser_feld_ablegen(selector, name)`: die Erweiterung liest den Feldwert SELBST und gibt ihn nur dem Server
 *     (server/extension-hub.ts `feldWertLesen`). Der Server merkt ihn in einer Gruppe vor (server/zugangswert-ablage.ts).
 *     Das Modell bekommt nur ok/fehler und einen festen Satz — nie den Wert, nie seine Länge.
 *  2. `zugangswerte_vorlegen()`: EINE Karte für die ganze Gruppe (z. B. SMTP-Benutzer + SMTP-Passwort) mit EINEM Knopf.
 *     Der Knopf schickt einen festen Wortlaut aus Kennung und Kurzwert des Servers; nur ein echter Tipp (isTrusted).
 *  3. Seitentext und alle anderen Ergebnisse der Erweiterung werden geschwärzt, bevor sie ein Modell sieht
 *     (`schwaerzeZugangswerte`); ein Bildschirmfoto einer Seite mit Zugangswert gibt es nicht.
 *  4. Geschwärzte Ergebnisse tragen die Marke `geschwaerzt: true` — sie werden nie gespeichert (`ohneGeheimeKarten`)
 *     und nie verdichtet (`istGeheimMarkiert` — die Regel für jede künftige Verdichtung).
 *
 * Nur feste Wörter und Zeichen — nie ein Wert. Die Wortlaute beginnen nicht mit „Ja“ (kein Ja für ein anderes Tor).
 */
import { GEHEIM, schwaerzeText } from "./geheim.js";

/** Notschalter: nur `ZUGANGSWERT_ABLAGE=1` schaltet ein (Vorgabe AUS). Je Aufruf gelesen — EINE Stelle für Server, Hub und Mac-Weg. */
export function zugangswertAblageAn(env: Readonly<Record<string, string | undefined>> = process.env): boolean {
  return env.ZUGANGSWERT_ABLAGE?.trim() === "1";
}

/** Das Werkzeug, mit dem die Erweiterung einen Feldwert direkt an den Server gibt. */
export const FELD_ABLEGEN = "browser_feld_ablegen";

/** Das Werkzeug, das die Karte der Gruppe anlegt — und der Name, unter dem die Karte im Zug steht. */
export const ZUGANGSWERTE_KARTE = "zugangswerte_vorlegen";

/** Form der Kennung einer Gruppe (6 Zufallsbytes als Hex — wie ein Vorschlag). */
export const ZUGANGSWERT_KENNUNG = /^[0-9a-f]{12}$/;

/** Form des Kurzwerts im Knopf-Wortlaut (8 Hex-Zeichen eines HMAC). */
export const ZUGANGSWERT_KURZWERT = /^[0-9a-f]{8}$/;

/** Höchstens so viele Werte in einer Gruppe (Benutzer, Passwort, Host, Port). */
export const ZUGANGSWERTE_HOECHSTENS = 4;

/** Eine Gruppe gilt höchstens so lange ab dem ersten Wert — absolut, nie verlängert, unabhängig von jedem Mandat. */
export const ZUGANGSWERT_FRIST_MS = 30 * 60_000;

/**
 * Der Zweck eines Werts — nur aus dieser festen Liste (Prüfbefund B9, Kann: nie freier Text des Modells). Die Karte zeigt ihn.
 */
export const ZUGANGSWERT_ZWECKE = {
  "smtp-benutzer": "SMTP-Benutzer",
  "smtp-passwort": "SMTP-Passwort",
  "smtp-host": "SMTP-Server",
  "smtp-port": "SMTP-Port",
  "zugangsschluessel-id": "Zugangsschlüssel-Kennung",
  "zugangsschluessel-geheim": "Geheimer Zugangsschlüssel",
  "api-schluessel": "API-Schlüssel",
  token: "Token",
  sonstiges: "Zugangswert aus dem Browser",
} as const;
export type ZugangswertZweck = keyof typeof ZUGANGSWERT_ZWECKE;
export const ZWECK_VORGABE: ZugangswertZweck = "sonstiges";
export function istZugangswertZweck(v: unknown): v is ZugangswertZweck {
  return typeof v === "string" && Object.hasOwn(ZUGANGSWERT_ZWECKE, v);
}

/** Der feste Wortlaut des Ablegen-Knopfs — EINE Stelle für Karte und Prüfung. */
export function zugangswerteKnopfJa(kennung: string, kurzwert: string): string {
  return `Zugangswerte ablegen (Ablage ${kennung}-${kurzwert})`;
}

/** Der Verwerfen-Knopf derselben Karte. */
export function zugangswerteKnopfNein(kennung: string): string {
  return `Zugangswerte verwerfen (Ablage ${kennung})`;
}

/** Sieht eine Nachricht aus wie einer der beiden Knöpfe (gleich welche Kennung)? */
export const ZUGANGSWERT_KNOPF_FORM = /^Zugangswerte (?:ablegen \(Ablage [0-9a-f]{12}-[0-9a-f]{8}\)|verwerfen \(Ablage [0-9a-f]{12}\))$/;

// ---------------------------------------------------------------------------------------------------------------------------------
// Schwärzung
// ---------------------------------------------------------------------------------------------------------------------------------

/** Wörter, die einen Zugangswert ankündigen — klein gelesen, auch als Teil („smtp-passwort“, „SecretAccessKey“). */
const ANKUENDIGUNG = /smtp|passw|passphrase|pwd|kennwort|secret|geheim|schl(?:ü|ue)ssel|key|token|credential|zugangs?daten|api[-_ ]?zugang/i;

/** AWS-Kennungen (Zugangsschlüssel, Rollen, Benutzer) — mit oder ohne Ziffer. */
const AWS_KENNUNG = /(?<![A-Z0-9])(?:AKIA|ASIA|AGPA|AIDA|AROA|ANPA|ANVA|AIPA)[A-Z0-9]{12,}(?![A-Z0-9])/g;

/** Eine Base64-ähnliche Folge ab 16 Zeichen (Standard- und Adress-Alphabet). */
const FOLGE = /(?<![A-Za-z0-9+/=_-])[A-Za-z0-9+/_-]{16,}={0,2}(?![A-Za-z0-9+/=_-])/g;

/** Base64 in Gruppen mit Leerzeichen („BFk3 Lx9v R2mQ 8sT1 …“): mindestens vier Gruppen von 2 bis 8 Zeichen. */
const FOLGE_MIT_LUECKEN = /(?<![A-Za-z0-9+/=])(?:[A-Za-z0-9+/]{2,8} ){3,}[A-Za-z0-9+/]{2,8}={0,2}(?![A-Za-z0-9+/=])/g;

/** Nach einer Ankündigung mit „:“ oder „=“: der nächste Wert ab 6 Zeichen, gleich wie er aussieht. */
const NACH_DOPPELPUNKT = new RegExp(`(${ANKUENDIGUNG.source})([\\w .-]{0,24}?)\\s*[:=]\\s*["'„“]?([^\\s"'„“”]{6,})`, "gi");

/** Ein Wort (ohne Leerraum) ab 6 Zeichen — im Fenster nach einer Ankündigung geprüft (`wortGemischt`). */
const WORT = /[^\s"'„“”<>]{6,}/g;

/** Ein HTML-Tag. */
const TAG = /<[a-zA-Z][^<>]*>/g;
/** Attribute, deren Wert in einem Tag mit Ankündigung geschwärzt wird (der Wert, nicht Name/Kennung des Feldes). */
const WERT_ATTRIBUTE = /(\b(?:value|content|alt|title|placeholder|data-[\w-]+)\s*=\s*)("[^"]*"|'[^']*'|[^\s>]+)/gi;

/** Sieht eine Folge nach Schlüssel aus (nicht nur ein langes Wort oder ein Name mit Bindestrichen)? */
function folgeVerdaechtig(f: string): boolean {
  const kern = f.replace(/=+$/, "");
  const ziffer = /[0-9]/.test(kern);
  const buchstabe = /[A-Za-z]/.test(kern);
  const gemischt = /[A-Z]/.test(kern) && /[a-z]/.test(kern);
  if (!buchstabe) return kern.length >= 20; // lange Ziffernfolge
  if (/[+/]/.test(kern)) return true; // Base64
  if (ziffer) return true;
  // ohne Ziffer: nur gemischte Schreibung ohne Bindestrich-Wörter (ein Kebab-Name ist kein Schlüssel)
  return gemischt && !/[-_]/.test(kern);
}

/**
 * Ein Wort mit gemischten Zeichenarten (Prüfbefund B9, Muss 1): Buchstabe UND Ziffer, Buchstabe UND Sonderzeichen (außer Bindestrich,
 * Punkt, Unterstrich am Wortrand), oder ein Großbuchstabe NACH einem Kleinbuchstaben („Fake9Pw!xQ“, „xQr7…“). „SMTP-Passwort“,
 * „Benutzername“ und „eu-central“ sind keine.
 */
function wortGemischt(w: string): boolean {
  if (w === GEHEIM || (ANKUENDIGUNG.test(w) && !/[0-9]/.test(w))) return false;
  const buchstabe = /\p{L}/u.test(w);
  if (!buchstabe) return /[0-9]/.test(w) && w.replace(/\D/g, "").length >= 6;
  if (/[0-9]/.test(w)) return true;
  if (/[!#$%&*+/=?^~|\\;@]/.test(w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))) return true;
  return /\p{Ll}\p{Lu}/u.test(w);
}

/**
 * Kyrillische und griechische Buchstaben, die wie lateinische aussehen — dieselbe Tabelle wie die rote Liste des Mandats
 * (server/mandat-rahmen.ts `rotVergleichsText`; hier ohne Abhängigkeit vom Server).
 */
const HOMOGLYPHEN: Readonly<Record<string, string>> = {
  а: "a", в: "b", е: "e", ё: "e", к: "k", м: "m", н: "h", о: "o", р: "p", с: "c", т: "t", у: "y", х: "x", ѕ: "s", і: "i", ї: "i",
  ј: "j", ԁ: "d", ӏ: "l", ԛ: "q", ԝ: "w", һ: "h", ү: "y",
  А: "A", В: "B", Е: "E", Ё: "E", К: "K", М: "M", Н: "H", О: "O", Р: "P", С: "C", Т: "T", У: "Y", Х: "X", Ѕ: "S", І: "I", Ј: "J",
  α: "a", β: "b", ε: "e", ι: "i", κ: "k", ν: "v", ο: "o", ρ: "p", τ: "t", υ: "u", χ: "x", γ: "y", ω: "w",
  Α: "A", Β: "B", Ε: "E", Ζ: "Z", Η: "H", Ι: "I", Κ: "K", Μ: "M", Ν: "N", Ο: "O", Ρ: "P", Τ: "T", Υ: "Y", Χ: "X",
};
const HOMOGLYPH_MUSTER = new RegExp(`[${Object.keys(HOMOGLYPHEN).join("")}]`, "gu");

/**
 * Der Text, wie die Erkennung ihn liest: NFKC, ohne unsichtbare Formatzeichen (Nullbreite, weiches Trennzeichen), Homoglyphen
 * lateinisch, `%3D`/`%3A`/`%20`/`%2B`/`%2F` entschlüsselt („password%3D…“).
 */
function normalisiert(t: string): string {
  return t
    .normalize("NFKC")
    .replace(/\p{Cf}/gu, "")
    .replace(HOMOGLYPH_MUSTER, (z) => HOMOGLYPHEN[z] ?? z)
    .replace(/%3D/gi, "=")
    .replace(/%3A/gi, ":")
    .replace(/%20/gi, " ")
    .replace(/%2B/gi, "+")
    .replace(/%2F/gi, "/")
    .normalize("NFKC");
}

/** Die Regeln auf EINEN Text (ohne Normalisierung). */
function schwaerzeRoh(text: string): string {
  let t = schwaerzeText(text).replace(AWS_KENNUNG, GEHEIM);
  // HTML: ein Tag, das eine Ankündigung trägt (id="smtp-password"), verliert die Werte seiner Wert-Attribute.
  t = t.replace(TAG, (tag) => (ANKUENDIGUNG.test(tag) ? tag.replace(WERT_ATTRIBUTE, (_g, vor: string) => `${vor}"${GEHEIM}"`) : tag));
  t = t.replace(NACH_DOPPELPUNKT, (treffer: string, ...gruppen: unknown[]) => {
    const wert = typeof gruppen[2] === "string" ? gruppen[2] : "";
    return wert === GEHEIM || /^https?:/i.test(wert) ? treffer : treffer.slice(0, treffer.length - wert.length) + GEHEIM;
  });
  // Fenster: die Zeile mit der Ankündigung (auch nach Tab oder mehreren Leerzeichen) und die zwei Zeilen danach.
  const zeilen = t.split("\n");
  let nachAnkuendigung = 0;
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i] as string;
    if (ANKUENDIGUNG.test(z)) nachAnkuendigung = 3;
    if (nachAnkuendigung > 0) {
      zeilen[i] = z
        .replace(FOLGE_MIT_LUECKEN, (f) => (/[0-9]/.test(f) && f.replace(/ /g, "").length >= 16 ? GEHEIM : f))
        .replace(FOLGE, (f) => (folgeVerdaechtig(f) ? GEHEIM : f))
        .replace(WORT, (w) => (wortGemischt(w) ? GEHEIM : w));
      nachAnkuendigung--;
    }
  }
  return zeilen.join("\n");
}

/**
 * Einen Text für das Modell schwärzen — lieber zu viel als zu wenig:
 *  - alles, was shared/geheim.ts erkennt (bekannte Anfänge, PEM, hohe Entropie ab 32 Zeichen);
 *  - AWS-Kennungen (AKIA…, ASIA…) überall;
 *  - in einer Zeile mit einer Ankündigung (SMTP, password, Passwort, secret, geheim, key, token …) und den zwei Zeilen danach jede
 *    Base64-ähnliche Folge ab 16 Zeichen (auch in Gruppen mit Leerzeichen) und jedes Wort ab 6 Zeichen mit gemischten Zeichenarten;
 *  - nach „<Ankündigung>: <Wert>“ oder „<Ankündigung>=<Wert>“ der Wert ab 6 Zeichen, gleich wie er aussieht;
 *  - in einem HTML-Tag mit Ankündigung die Werte von value, content, alt, title, placeholder, data-*.
 * Trifft die Erkennung erst am normalisierten Text (Nullbreite, Homoglyphen, %-Kodierung), kommt der normalisierte Text geschwärzt
 * zurück — sonst bleibt der Text, wie er war.
 */
export function schwaerzeZugangswerte(text: string): string {
  if (typeof text !== "string" || text.length < 6) return text;
  const roh = schwaerzeRoh(text);
  const n = normalisiert(text);
  if (n === text) return roh;
  const nGeschwaerzt = schwaerzeRoh(n);
  return nGeschwaerzt !== n ? nGeschwaerzt : roh;
}

/** Steht in diesem Text ein Zugangswert (nach den Regeln oben)? */
export function enthaeltZugangswert(text: unknown): boolean {
  return typeof text === "string" && schwaerzeZugangswerte(text) !== text;
}

/** Felder, die als Bild nie geschwärzt werden (base64) — dessen Prüfung läuft vorher am Seiten- bzw. Fenstertext. */
const BILD_FELDER = new Set(["base64", "data"]);
/** Nur ein echtes Bild bleibt: lang und ohne Leerraum — ein kurzer Text unter diesem Namen wird geprüft wie jeder andere. */
const istBild = (v: unknown): boolean => typeof v === "string" && v.length >= 1000 && !/\s/.test(v.slice(0, 2000));

/** Felder, die einen WERT tragen — ganz geschwärzt, wenn ein Geschwisterfeld oder ihr eigener Name eine Ankündigung trägt. */
const WERT_FELDER = new Set(["wert", "value", "text", "inhalt", "content", "val"]);
/** Felder, die ein Feld BESCHREIBEN (Beschriftung, Name, Selektor …). */
const BESCHRIFTUNG_FELDER = new Set(["beschriftung", "label", "name", "selektor", "selector", "id", "key", "titel", "title", "placeholder", "aria", "rolle", "bezeichnung", "feld"]);

/** So meldet die Formular-Übersicht (extension/content/page-scripts/form.js) ein Kennwortfeld, das sie NICHT gelesen hat — kein Wert. */
const NICHT_GELESEN = "(Kennwort-/Kartenfeld, nicht gelesen)";

const TIEFE_MAX = 8;

function istText(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

function tief(w: unknown, tiefe: number, treffer: { n: number }): unknown {
  if (typeof w === "string") {
    if (w === NICHT_GELESEN) return w;
    const s = schwaerzeZugangswerte(w);
    if (s !== w) treffer.n++;
    return s;
  }
  if (w === null || typeof w !== "object") return w;
  if (tiefe > TIEFE_MAX) {
    treffer.n++;
    return GEHEIM;
  }
  if (Array.isArray(w)) return w.map((x) => tief(x, tiefe + 1, treffer));
  const art = Object.getPrototypeOf(w) as unknown;
  if (art !== Object.prototype && art !== null) return w;
  const o = w as Record<string, unknown>;
  // Prüfbefund B9 (Muss 1): eine Beschriftung „SMTP-Passwort“ neben `wert` — der Wert fällt ganz, gleich wie kurz oder schlicht er ist.
  const beschriftet = Object.entries(o).some(([k, v]) => BESCHRIFTUNG_FELDER.has(k.toLowerCase()) && istText(v) && ANKUENDIGUNG.test(normalisiert(v)));
  const neu: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (BILD_FELDER.has(k) && istBild(v)) {
      neu[k] = v;
      continue;
    }
    // JSON wie {"SmtpPassword": "…"}: der Schlüsselname selbst kündigt an (nicht unsere eigenen Marken wie `geschwaerzt`).
    const eigenerName = ANKUENDIGUNG.test(normalisiert(k)) && k !== "geschwaerzt";
    if (istText(v) && v !== GEHEIM && v !== NICHT_GELESEN && ((beschriftet && WERT_FELDER.has(k.toLowerCase())) || eigenerName)) {
      treffer.n++;
      neu[k] = GEHEIM;
      continue;
    }
    neu[k] = tief(v, tiefe + 1, treffer);
  }
  return neu;
}

/**
 * Ein Ergebnis (Erweiterung, Mac) schwärzen, bis in jede Tiefe (außer `base64`/`data`). Wurde etwas geschwärzt, trägt es die
 * Marke `geschwaerzt: true` — ein Objekt direkt, eine Liste oder ein Text eingepackt als `{ ergebnis, geschwaerzt: true }`.
 * Das Original wird nie verändert.
 */
export function schwaerzeErgebnis(ergebnis: unknown): unknown {
  const treffer = { n: 0 };
  const neu = tief(ergebnis, 0, treffer);
  if (treffer.n === 0) return neu;
  if (neu && typeof neu === "object" && !Array.isArray(neu)) return { ...(neu as Record<string, unknown>), geschwaerzt: true };
  return { ergebnis: neu, geschwaerzt: true };
}

/** Ist dieses Ergebnis (Objekt oder JSON-Text) als geheim markiert? Dann wird es nie gespeichert und nie verdichtet. */
export function istGeheimMarkiert(ergebnis: unknown): boolean {
  let e = ergebnis;
  for (let i = 0; i < 3 && typeof e === "string"; i++) {
    if (!e.includes("geschwaerzt")) return false;
    try {
      e = JSON.parse(e) as unknown;
    } catch {
      return false;
    }
  }
  return !!e && typeof e === "object" && (e as { geschwaerzt?: unknown }).geschwaerzt === true;
}

/** Was statt eines geheim markierten Ergebnisses gespeichert wird. */
export const GEHEIM_NICHT_GESPEICHERT = Object.freeze({
  geschwaerzt: true,
  hinweis: "Auf der Seite stand ein Zugangswert — dieses Ergebnis wird nicht gespeichert.",
});

/**
 * Die Werkzeug-Karten einer Nachricht ohne geheim markierte Ergebnisse (memory/store-sqlite.ts, vor dem Schreiben). Andere
 * Metadaten bleiben, wie sie sind; das Original wird nie verändert.
 */
export function ohneGeheimeKarten<T>(metadata: T): T {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return metadata;
  const karten = (metadata as { toolCards?: unknown }).toolCards;
  if (!Array.isArray(karten) || !karten.some((k) => istGeheimMarkiert((k as { result?: unknown } | null)?.result))) return metadata;
  return {
    ...metadata,
    toolCards: karten.map((k) => {
      if (!k || typeof k !== "object" || !istGeheimMarkiert((k as { result?: unknown }).result)) return k;
      const { ok } = ((): { ok?: unknown } => {
        const r = (k as { result?: unknown }).result;
        try {
          return (typeof r === "string" ? JSON.parse(r) : r) as { ok?: unknown };
        } catch {
          return {};
        }
      })();
      return { ...(k as Record<string, unknown>), result: { ...(typeof ok === "boolean" ? { ok } : {}), ...GEHEIM_NICHT_GESPEICHERT } };
    }),
  };
}
